"use client";

import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { AGE_ADJUSTED_RATE_CALCULATOR_STRINGS } from "@/components/tools/age-adjusted-rate-calculator";
import {
  DataGrid,
  WorkingLine,
} from "@/components/tools/stats-clinical/data-grid";
import { t } from "@/lib/i18n/interpolate";
import type { AdjustedRateResult } from "@/lib/tool-kit/stats-clinical/age-adjusted";
import { type Formatter, fixed } from "@/lib/tool-kit/stats-research/format";

/*
 * The result panel of the age-adjusted rate calculator. Strings are the widget's own.
 */

type Strings = typeof AGE_ADJUSTED_RATE_CALCULATOR_STRINGS;

export interface Alternative {
  name: string;
  result: AdjustedRateResult;
}

function rate(fmt: Formatter, value: number): string {
  return fixed(fmt, value, 1);
}

export function AdjustedSummary({
  result,
  standardName,
  baseText,
  alternatives,
  strings,
  fmt,
}: {
  result: AdjustedRateResult;
  standardName: string;
  baseText: string;
  alternatives: readonly Alternative[];
  strings: Strings;
  fmt: Formatter;
}) {
  const level = fmt(result.level * 100, { maximumFractionDigits: 1 });
  const interval = `${rate(fmt, result.lower)} – ${rate(fmt, result.upper)}`;
  const report = t(strings.report, {
    adjusted: rate(fmt, result.adjusted),
    level,
    lower: rate(fmt, result.lower),
    upper: rate(fmt, result.upper),
    base: baseText,
    standard: standardName,
    crude: rate(fmt, result.crude),
  });
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.cardAdjusted, { base: baseText })}
          value={rate(fmt, result.adjusted)}
        />
        <StatCard label={t(strings.cardInterval, { level })} value={interval} />
        <StatCard label={strings.cardCrude} value={rate(fmt, result.crude)} />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings.reading, {
          adjusted: rate(fmt, result.adjusted),
          crude: rate(fmt, result.crude),
          base: baseText,
          standard: standardName,
          cases: fmt(result.totalCases),
        })}
      </p>
      <DataGrid
        head={[
          strings.columnAge,
          strings.columnCases,
          strings.columnPopulation,
          t(strings.columnRate, { base: baseText }),
          strings.columnWeight,
          strings.columnContribution,
        ]}
        rows={[
          ...result.groups.map((g) => [
            g.label,
            fmt(g.cases),
            fmt(g.population),
            fixed(fmt, g.rate, 2),
            fixed(fmt, g.weight, 4),
            fixed(fmt, g.contribution, 2),
          ]),
          [
            strings.total,
            fmt(result.totalCases),
            fmt(result.totalPopulation),
            rate(fmt, result.crude),
            "1.0000",
            fixed(fmt, result.adjusted, 2),
          ],
        ]}
      />
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.alternativesHeading}</h3>
        <p className="text-muted-foreground text-sm">
          {strings.alternativesNote}
        </p>
        <DataGrid
          head={[
            strings.columnStandard,
            t(strings.columnAdjusted, { base: baseText }),
            t(strings.columnInterval, { level }),
          ]}
          rows={alternatives.map((a) => [
            a.name,
            rate(fmt, a.result.adjusted),
            `${rate(fmt, a.result.lower)} – ${rate(fmt, a.result.upper)}`,
          ])}
        />
      </section>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <span className="font-mono text-muted-foreground text-xs">
          {report}
        </span>
      </div>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
        <WorkingLine>{strings.workingRate}</WorkingLine>
        <WorkingLine>
          {t(strings.workingSe, {
            se: fixed(fmt, result.se, 2),
            base: baseText,
          })}
        </WorkingLine>
        <p className="text-muted-foreground text-sm">{strings.workingNote}</p>
      </div>
    </div>
  );
}
